import React from "react";
import { Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
interface ProductBasedRiskBarChartProps {
  productData?: any; // Replace with proper type when API is defined
  isLoading?: boolean;
  error?: any;
}
const ProductBasedRiskBarChart: React.FC<ProductBasedRiskBarChartProps> = ({ 
  productData,
  isLoading = false,
  error = null
}) => {
  const theme = useTheme();
  
  // Sample data - Replace with actual API data when available
  const mockData = {
    labels: ['High', 'Medium', 'Medium-High', 'Medium-Low'],
    datasets: [
      {
        label: 'REVERSED',
        data: [15000000, 5000000, 2000000, 1000000],
        riskLevel: 'High'
      },
      {
        label: 'STANDARD',
        data: [4000000, 5000000, 0, 2000000],
        riskLevel: 'Medium'
      },
      {
        label: 'ENHANCED',
        data: [0, 0, 2500000, 0],
        riskLevel: 'Medium-High'
      }
    ]
  };
  
  const createChartData = () => {
    // Use actual API data when available
    const data = mockData;
    
    const colors = {
      REVERSED: '#1976D2', // Blue
      STANDARD: '#388E3C', // Green
      ENHANCED: '#FFA000', // Amber
    };
    
    return {
      labels: data.labels,
      datasets: data.datasets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: colors[dataset.label as keyof typeof colors] || '#757575',
        borderColor: theme.palette.background.paper,
        borderWidth: 1,
        borderRadius: 4,
        categoryPercentage: 0.8,
        barPercentage: 0.8,
      })),
    };
  };
  const chartData = createChartData();
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load product risk data</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ height: "calc(100% - 50px)" }}>
        <Bar 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  color: theme.palette.text.primary,
                  font: {
                    weight: 'bold'
                  },
                  boxWidth: 10
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    const label = context.dataset.label || '';
                    return `${label}: $${context.parsed.y.toLocaleString()}`;
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: {
                  color: theme.palette.divider,
                },
                ticks: {
                  callback: function (value) {
                    return `$${(Number(value) / 1000000).toFixed(1)}M`;
                  }
                }
              },
              x: {
                grid: {
                  display: false,
                }
              }
            },
            animation: {
              duration: 1000
            }
          }} 
        />
      </Box>
    </Box>
  );
};
export default ProductBasedRiskBarChart;
